import { environmentConfig } from "@src/appV2/environment";

enum FACILITY_ROLES {
  ADMIN = "ADM",
  SHIFT_MANAGEMENT = "SFT",
  INVOICES = "INV",
  DOCUMENTS = "DMT",
  RECRUITMENT = "REC",
  CORPORATE_ADMIN = "CORP",
}

const FACILITY_USER_PERMISSIONS = {
  POST_SHIFT_PERMISSION: "post-shifts",
  CANCEL_SHIFT_PERMISSION: "cancel-shifts",
  MANAGE_INVOICES: "manage-invoices",
  ACCESS_UPCOMING_CHARGES: "access-upcoming-charges",
  ACCESS_CONTRACT_TERMS: "access-contract-terms",
  SEE_CHARGE_RATES: "see-charge-rates",
  DISPUTE_UPCOMING_CHARGES: "dispute-upcoming-charges",
  EDIT_SHIFT_RATE: "edit-shift-rate",
  REVIEW_MISSED_PUNCH_REQUESTS: "review-missed-punch-requests",
  REVIEW_BREAK_REQUESTS: "review-break-requests",
  SET_IN_HOME_VISIT_RATE: "set-in-home-visit-rate",
  MANAGE_CHARTING: "manage-charting",
  ADD_DNR: "add-dnr",
  REMOVE_DNR: "remove-dnr",
  ACCESS_REPORTING: "access-reporting",
} as const;

type FacilityUserPermission =
  (typeof FACILITY_USER_PERMISSIONS)[keyof typeof FACILITY_USER_PERMISSIONS];

const FACILITY_USER_PERMISSION_LABELS: Record<FacilityUserPermission, string> = {
  [FACILITY_USER_PERMISSIONS.POST_SHIFT_PERMISSION]: "Can post shifts",
  [FACILITY_USER_PERMISSIONS.CANCEL_SHIFT_PERMISSION]: "Can cancel shifts",
  [FACILITY_USER_PERMISSIONS.MANAGE_INVOICES]: "Can view and manage invoices",
  [FACILITY_USER_PERMISSIONS.ACCESS_UPCOMING_CHARGES]: "Can access upcoming charges",
  [FACILITY_USER_PERMISSIONS.ACCESS_CONTRACT_TERMS]: "Can view contract terms",
  [FACILITY_USER_PERMISSIONS.SEE_CHARGE_RATES]: "Can see charge rates",
  [FACILITY_USER_PERMISSIONS.DISPUTE_UPCOMING_CHARGES]: "Can dispute upcoming charges",
  [FACILITY_USER_PERMISSIONS.EDIT_SHIFT_RATE]: "Can edit shift rate",
  [FACILITY_USER_PERMISSIONS.REVIEW_MISSED_PUNCH_REQUESTS]: "Can review missed punch requests",
  [FACILITY_USER_PERMISSIONS.REVIEW_BREAK_REQUESTS]: "Can review break requests",
  [FACILITY_USER_PERMISSIONS.SET_IN_HOME_VISIT_RATE]: "Can set home health visit rates",
  [FACILITY_USER_PERMISSIONS.MANAGE_CHARTING]: "Can manage charting",
  [FACILITY_USER_PERMISSIONS.ADD_DNR]: "Add DNR",
  [FACILITY_USER_PERMISSIONS.REMOVE_DNR]: "Remove DNR",
  [FACILITY_USER_PERMISSIONS.ACCESS_REPORTING]: "Can access reporting",
};

// facility role to user facing value mapping
// in db we are using the same roles
// but changing the user facing value.
const FACILITY_ROLE_MAPPING = new Map([
  ["ADMIN", "ADMIN"],
  ["SHIFT_MANAGEMENT", "SHIFT_MANAGEMENT"],
  ["DOCUMENTS", "SUPERVISOR"],
  ["INVOICES", "INVOICES"],
  ["RECRUITMENT", "RECRUITMENT"],
  ["CORPORATE_ADMIN", "CORPORATE_ADMIN"],
]);

export enum ALERT_TYPE {
  EMAIL = "EMAIL",
  SMS = "SMS",
}

const CHARGE_RATE_VISIBLE_EMAIL = "CHARGE_RATE_VISIBLE_EMAIL";
const WORKER_ETA_SMS = "WORKER_ETA_SMS";

const FACILITY_ROLE_DESCRIPTIONS = {
  [FACILITY_ROLES.ADMIN]:
    "This permission gives the user access to all permissions and additionally gives the user the ability to edit and remove additional users for this facility.",
  [FACILITY_ROLES.SHIFT_MANAGEMENT]:
    "This permission gives the user the ability to request and cancel shifts.",
  [FACILITY_ROLES.DOCUMENTS]:
    "This permission allows the user to view all shifts and worker ETA but does not allow to manage the schedule.",
  [FACILITY_ROLES.INVOICES]:
    "This permission means that this user will receive a copy of the invoice via email.",
  [FACILITY_ROLES.RECRUITMENT]:
    "This permission allows the user to access everything related to recruitment.",
  [FACILITY_ROLES.CORPORATE_ADMIN]:
    "This permission allows the user to access corporate-level features including Corporate Analytics and Spending Controls.",
};

const HOLIDAY_INPUT_PROPS = {
  FLAT_PROPS: {
    description: "How much is that flat increase?",
    label: " dollars per hour",
  },
  MULTIPLIER_PROPS: {
    description: "How much is that Multiplier increase?",
    label: " times the charge rate",
  },
};

const HOLIDAY_DIFFERENTIAL = {
  FLAT: "FLAT",
  CHARGE_RATE_MULTIPLIER: "CHARGE_RATE_MULTIPLIER",
};

function truncateToTwoDecimals(value: number): number {
  return Math.floor(value * 100) / 100;
}

// Mirrors the `managementSection.teamMembers` privilege check used by the
// route at /workplace/users; see WorkplaceMenuPrivilegesProvider.ts. Keep in
// sync with the route's authorization, not narrowed independently.
function canAccessTeamMembersRoute(args: {
  isAdmin: boolean;
  userAccess: readonly string[] | undefined;
}): boolean {
  if (args.isAdmin) {
    return true;
  }
  const access = args.userAccess;
  if (!access) {
    return false;
  }
  return (
    access.includes(FACILITY_ROLES.ADMIN) ||
    access.includes(FACILITY_ROLES.SHIFT_MANAGEMENT) ||
    access.includes(FACILITY_ROLES.INVOICES)
  );
}

const RATES_TABLE = {
  sunday: { am: 0, pm: 0, noc: 0 },
  monday: { am: 0, pm: 0, noc: 0 },
  tuesday: { am: 0, pm: 0, noc: 0 },
  wednesday: { am: 0, pm: 0, noc: 0 },
  thursday: { am: 0, pm: 0, noc: 0 },
  friday: { am: 0, pm: 0, noc: 0 },
  saturday: { am: 0, pm: 0, noc: 0 },
};

const PERMISSIONS = {
  NOTES: { NOTE_EDIT: "NOTE_EDIT" },
};

const INSTANT_BOOK_TYPE = {
  ON: "ON",
  OFF: "OFF",
  PAST_HCPS_ONLY: "PAST_HCPS_ONLY",
};

const batchDescription = {
  EMAIL: "Batch all emails in the preceding 24 hours and send to me at this time",
  SMS: "Batch all Text Messages in the preceding 24 hours and send to me at this time",
};

const hours = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

const FACILITY_PREFERENCE_BASE_URL = `${environmentConfig.REACT_APP_SHIFT_VERIFICATION_BASE_URL}/facilities`;

const FACILITY_HELP_SUPPORT_LINK =
  environmentConfig.REACT_APP_FACILITY_SUPPORT_ARTICLES_LINK ||
  "https://support.clipboardhealth.com/hc/en-us/categories/4943215926167-Facilities";

const DEFAULT_BASE_CHARGE_LIMIT = 250;

export {
  FACILITY_ROLES,
  FACILITY_ROLE_MAPPING,
  INSTANT_BOOK_TYPE,
  FACILITY_ROLE_DESCRIPTIONS,
  HOLIDAY_DIFFERENTIAL,
  HOLIDAY_INPUT_PROPS,
  truncateToTwoDecimals,
  canAccessTeamMembersRoute,
  RATES_TABLE,
  PERMISSIONS,
  batchDescription,
  hours,
  FACILITY_PREFERENCE_BASE_URL,
  CHARGE_RATE_VISIBLE_EMAIL,
  WORKER_ETA_SMS,
  FACILITY_HELP_SUPPORT_LINK,
  FACILITY_USER_PERMISSIONS,
  FACILITY_USER_PERMISSION_LABELS,
  DEFAULT_BASE_CHARGE_LIMIT,
};

export type { FacilityUserPermission };
